import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  Space,
  Popconfirm,
  message,
  Card,
  Tag,
  Tooltip,
  Row,
  Col,
  Tabs,
  Statistic,
  Switch,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  SettingOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { serverService, Service as ExtendedService, CreateServiceRequest as ExtendedCreateServiceRequest } from '../services/serverService';
import { companyService, Company } from '../services/companyService';
import { serviceService } from '../services/serviceService';
import ServiceParamManager from './ServiceParamManager';
import TimeDisplay from './TimeDisplay';
import './WhatsAppConnectorManager.css';
import { useI18n } from '../hooks/useI18n';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

// 使用从serviceService导入的Service接口

// 使用从serviceService导入的接口

interface ServiceManagerProps {
  defaultActiveTab?: string;
}

const ServiceManager: React.FC<ServiceManagerProps> = ({ defaultActiveTab = 'services' }) => {
  const [services, setServices] = useState<ExtendedService[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingService, setEditingService] = useState<ExtendedService | null>(null);
  const [showParamManager, setShowParamManager] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [selectedServiceName, setSelectedServiceName] = useState<string>('');
  const [form] = Form.useForm();
  // const [testLoading, setTestLoading] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [formActiveTab, setFormActiveTab] = useState('basic');
  const { t } = useI18n();
  
  // 监听认证类型变化
  const authType = Form.useWatch('auth_type', form);
  
  // 统计数据
  const [stats, setStats] = useState({
    services: { total: 0, active: 0 }
  });



  // 加载统计数据
  const loadStats = async () => {
    setLoading(true);
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      
      const [servicesRes, activeServicesRes] = await Promise.all([
        serverService.getServices({ tenant_code: tenantCode, limit: 1 }),
        serverService.getServices({ tenant_code: tenantCode, is_active: true, limit: 1 })
      ]);

      setStats({
        services: {
          total: servicesRes.total || 0,
          active: activeServicesRes.total || 0
        }
      });
    } catch (error: unknown) {
      console.error('加载统计数据失败:', error);
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error(t('services.messages.loadStatsFailed') + ': ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 加载服务列表
  const loadServices = async () => {
    setLoading(true);
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const response = await serverService.getServices({ tenant_code: tenantCode });
      setServices(response.data || []);
    } catch (error: unknown) {
      console.error('加载服务失败:', error);
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error(t('services.messages.loadServicesFailed') + ': ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 加载公司列表（用于绑定 project_id）
  const loadCompanies = async () => {
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const res = await companyService.getCompanies(tenantCode);
      setCompanies(res.data || []);
    } catch (error) {
      // 公司列表加载失败不影响服务管理的核心功能
      setCompanies([]);
    }
  };

  useEffect(() => {
    loadStats();
    loadServices();
    loadCompanies();
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'services') {
      loadServices();
    } else if (key === 'overview') {
      loadStats(); // 切换标签时刷新统计数据
    }
  };

  // 打开创建/编辑模态框
  const openModal = (service?: ExtendedService | null) => {
    setEditingService(service || null);
    setModalVisible(true);
    if (service) {
      form.setFieldsValue(service);
    } else {
      form.resetFields();
      form.setFieldsValue({
        tenant_code: localStorage.getItem('currentTenant') || 'default',
        auth_type: 'api_key',
        is_active: true
      });
      // 加载服务名称选项并设置默认值
      serverService.getServiceNameOptions().then((names) => {
        if (Array.isArray(names) && names.length > 0) {
          form.setFieldsValue({ service_name: names[0] });
        }
      }).catch(() => {});
    }
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingService(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';
      const serviceData: ExtendedCreateServiceRequest = {
        tenant_code: tenantCode,
        service_name: values.service_name as string,
        service_type: values.service_type as 'ai_platform' | 'webhook' | 'api' | 'database' | 'custom',
        service_url: values.service_url as string,
        auth_type: values.auth_type as 'api_key' | 'oauth2' | 'bearer' | 'basic' | 'app_key',
        ...values,
      };

      if (editingService) {
        // 更新服务
        // 注意：清空 Select 后 project_id 可能变成 undefined，后端会忽略 undefined 导致无法清空旧值
        const projectId = form.getFieldValue('project_id') as string | number | null | undefined;
        const rawXAppId = form.getFieldValue('x_app_id') as string | undefined;
        const rawXAppKey = form.getFieldValue('x_app_key') as string | undefined;
        const x_app_id =
          rawXAppId === undefined || rawXAppId === null || String(rawXAppId).trim() === ''
            ? null
            : String(rawXAppId).trim();
        const x_app_key =
          rawXAppKey === undefined || rawXAppKey === null || String(rawXAppKey).trim() === ''
            ? null
            : String(rawXAppKey).trim();
        await serverService.updateService(editingService.id!, {
          ...values,
          project_id: projectId ?? null,
          x_app_id,
          x_app_key,
        });
        message.success('服务更新成功');
      } else {
        // 创建服务
        await serverService.createService(serviceData);
        message.success('服务创建成功');
      }
      closeModal();
      loadServices();
      loadStats();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error('操作失败: ' + errorMessage);
    }
  };



  // 打开参数管理
  const openParamManager = (service: ExtendedService) => {
    setSelectedServiceId(service.id!);
    setSelectedServiceName(service.service_name);
    setShowParamManager(true);
  };

  // 关闭参数管理
  const closeParamManager = () => {
    setShowParamManager(false);
    setSelectedServiceId(null);
    setSelectedServiceName('');
  };

  // 删除服务
  const handleDelete = async (id: number) => {
    try {
      await serverService.deleteService(id);
      message.success('服务删除成功');
      loadServices();
      loadStats();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error('删除失败: ' + errorMessage);
    }
  };

  // 复制服务（含参数）
  const handleCopyService = async (service: ExtendedService) => {
    try {
      const tenantCode = localStorage.getItem('currentTenant') || 'default';

      const sourceParams = service.id ? await serviceService.getServiceParams(service.id) : [];
      const newServiceName = `${service.service_name}_copy_${new Date()
        .toISOString()
        .slice(11, 19)
        .replace(/:/g, '')}`;

      const createRes = await serverService.createService({
        tenant_code: tenantCode,
        project_id: (service as any).project_id ?? (service as any).company_id ?? null,
        service_name: newServiceName,
        service_type: (service as any).service_type,
        service_url: service.service_url,
        description: (service as any).description,
        auth_type: (service as any).auth_type,
        api_key: (service as any).api_key,
        x_app_id: (service as any).x_app_id,
        x_app_key: (service as any).x_app_key,
        client_id: (service as any).client_id,
        client_secret: (service as any).client_secret,
        oauth2_token_url: (service as any).oauth2_token_url,
        oauth2_scope: (service as any).oauth2_scope,
        oauth2_redirect_uri: (service as any).oauth2_redirect_uri,
        username: (service as any).username,
        password: (service as any).password,
        bearer_token: (service as any).bearer_token,
        additional_headers: (service as any).additional_headers,
        timeout_seconds: (service as any).timeout_seconds,
        retry_count: (service as any).retry_count,
        platform_type: (service as any).platform_type,
        app_name: (service as any).app_name,
        app_id: (service as any).app_id,
        home_page_link: (service as any).home_page_link,
        health_link: (service as any).health_link,
        is_active: (service as any).is_active ?? true,
        is_file_storage_provider: (service as any).is_file_storage_provider,
      } as any);

      const newServiceId = (createRes as any)?.data?.id;
      if (!newServiceId) throw new Error('复制失败：未返回新服务ID');

      for (const p of sourceParams) {
        await serviceService.createServiceParam(newServiceId, {
          service_id: newServiceId,
          param_name: p.param_name,
          param_value: p.param_value,
          param_type: p.param_type,
          description: p.description,
          is_required: p.is_required,
          sort_order: p.sort_order,
        });
      }

      message.success(t('services.messages.copySuccess'));
      loadServices();
      loadStats();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error(t('services.messages.copyFailed') + ': ' + errorMessage);
    }
  };

  // 测试连接
  // const handleTestConnection = async (id: number) => {
  //   setTestLoading(id);
  //   try {
  //     // TODO: 实现服务连接测试功能
  //     message.info('连接测试功能待实现');
  //   } catch (error: unknown) {
  //     const errorMessage = error instanceof Error ? error.message : '未知错误';
  //     message.error('连接测试失败: ' + errorMessage);
  //   } finally {
  //     setTestLoading(null);
  //   }
  // };

  // 处理服务健康检查
  const handleHealthCheck = async (id: number) => {
    try {
      const response = await serverService.performHealthCheck(id);
      if (response.success) {
        message.success('健康检查完成');
        loadServices();
      } else {
        message.error('健康检查失败');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      message.error('健康检查失败: ' + errorMessage);
    }
  };

  // 服务表格列定义
  const serviceColumns = [
    {
      title: t('services.table.company'),
      dataIndex: 'project_id',
      key: 'project_id',
      width: 160,
      render: (projectId: string | number | null | undefined) => {
        if (projectId === undefined || projectId === null || String(projectId).trim() === '') return '-';
        // project_id 映射公司名
        const matches = companies.filter(
          (c) =>
            c.project_id !== undefined &&
            c.project_id !== null &&
            String(c.project_id) === String(projectId)
        );
        if (matches.length === 0) return String(projectId);
        return matches.map((m) => m.company_name).filter(Boolean).join(', ');
      },
    },
    {
      title: t('services.table.name'),
      dataIndex: 'service_name',
      key: 'service_name',
      width: 200,
    },
    {
      title: t('services.table.authType'),
      dataIndex: 'auth_type',
      key: 'auth_type',
      width: 100,
      render: (type: string) => {
        const colorMap: Record<string, string> = {
          'api_key': 'green',
          'oauth2': 'blue',
          'bearer': 'purple',
          'basic': 'orange',
          'app_key': 'cyan'
        };
        const label =
          type === 'api_key' ? t('services.form.authTypeOptions.api_key') :
          type === 'oauth2' ? t('services.form.authTypeOptions.oauth2') :
          type === 'bearer' ? t('services.form.authTypeOptions.bearer') :
          type === 'basic' ? t('services.form.authTypeOptions.basic') :
          type === 'app_key' ? t('services.form.authTypeOptions.app_key') :
          type;
        return <Tag color={colorMap[type] || 'default'}>{label}</Tag>;
      },
    },
    {
      title: t('services.table.url'),
      dataIndex: 'service_url',
      key: 'service_url',
      ellipsis: true,
      width: 200,
    },
    {
      title: t('services.table.homePageLink'),
      dataIndex: 'home_page_link',
      key: 'home_page_link',
      width: 120,
      render: (link: string, record: ExtendedService) => {
        if (!link) return '-';
        const fullUrl = record.service_url + link;
        return (
          <Button
            type="link"
            size="small"
            onClick={() => window.open(fullUrl, '_blank')}
          >
            {t('services.table.openHomePage')}
          </Button>
        );
      },
    },
    {
      title: t('services.table.healthLink'),
      dataIndex: 'health_link',
      key: 'health_link',
      width: 140,
      render: (link: string, record: ExtendedService) => {
        if (!link) return '-';
        const fullUrl = record.service_url + link;
        return (
          <Button
            type="link"
            size="small"
            onClick={() => window.open(fullUrl, '_blank')}
          >
            {t('services.table.openHealthCheck')}
          </Button>
        );
      },
    },
    {
      title: t('services.table.health'),
      dataIndex: 'health_status',
      key: 'health_status',
      width: 100,
      render: (status: string) => {
        if (status === 'healthy') {
          return <Badge status="success" text={t('services.table.healthHealthy')} />;
        } else if (status === 'unhealthy') {
          return <Badge status="error" text={t('services.table.healthUnhealthy')} />;
        } else {
          return <Badge status="default" text={t('services.table.healthUnknown')} />;
        }
      },
    },
    {
      title: t('services.table.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? t('services.table.statusActive') : t('services.table.statusInactive')}
        />
      ),
    },
    {
      title: '文件存储提供商',
      dataIndex: 'is_file_storage_provider',
      key: 'is_file_storage_provider',
      width: 120,
      render: (isFileStorageProvider: boolean) => (
        <Tag color={isFileStorageProvider ? 'green' : 'default'}>
          {isFileStorageProvider ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: t('services.table.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-',
    },
    {
      title: t('services.table.actions'),
      key: 'action',
      width: 240,
      render: (_: unknown, record: ExtendedService) => (
        <Space size="small">
          <Tooltip title={t('services.buttons.edit')}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => openModal(record)}
            />
          </Tooltip>
          <Tooltip title={t('services.buttons.copy')}>
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleCopyService(record)}
            />
          </Tooltip>
          <Tooltip title={t('services.buttons.healthCheck')}>
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleHealthCheck(record.id!)}
            />
          </Tooltip>
          <Tooltip title={t('services.buttons.paramManager')}>
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => openParamManager(record)}
            />
          </Tooltip>
          <Tooltip title={t('services.buttons.delete')}>
            <Popconfirm
              title={t('services.confirm.delete')}
              onConfirm={() => handleDelete(record.id!)}
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];


  return (
    <div className="whatsapp-connector-container">
      <Card className="whatsapp-connector-card">
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: '#52c41a' }}>{t('services.title')}</h2>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            {t('services.subtitle')}
          </p>
        </div>

        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          className="service-tabs"
        >
          <TabPane 
            tab={
              <span>
                <DashboardOutlined />
                {t('services.tabs.overview')}
              </span>
            } 
            key="overview"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title={t('services.stats.services')}
                    value={stats.services.total}
                    suffix={t('services.stats.activeSuffix', { active: stats.services.active })}
                    prefix={<ApiOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>

            <Card 
              title={t('services.quickActions')} 
              style={{ marginTop: 16 }}
              extra={
                <Button 
                  type="primary" 
                  onClick={loadStats}
                  loading={loading}
                >
                  {t('common.refresh')}
                </Button>
              }
            >
              <Space wrap>
                <Button 
                  icon={<ApiOutlined />}
                  onClick={() => setActiveTab('services')}
                >
                  {t('services.manageServices')}
                </Button>
              </Space>
            </Card>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <ApiOutlined />
                {t('services.tabs.services')}
              </span>
            } 
            key="services"
          >
            <div>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0 }}>{t('services.title')}</h3>
                  <p style={{ margin: '8px 0 0 0', color: '#666' }}>
                    {t('services.subtitle')}
                  </p>
                </div>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadServices}
                    loading={loading}
                  >
                    {t('common.refresh')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openModal()}
                  >
                    {t('services.buttons.create')}
                  </Button>
                </Space>
              </div>

              <Table
                columns={serviceColumns}
                dataSource={services}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => t('common.paginationTotal', { from: range[0], to: range[1], total }),
                  pageSize: 10,
                }}
                scroll={{ x: 1200 }}
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* 服务创建/编辑模态框 */}
      <Modal
        title={
          <Space>
            <ApiOutlined />
            {editingService ? t('services.modal.editTitle') : t('services.modal.createTitle')}
          </Space>
        }
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Tabs activeKey={formActiveTab} onChange={setFormActiveTab}>
            <TabPane tab={t('services.form.basic')} key="basic">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="project_id"
                    label={t('services.form.company')}
                  >
                    <Select
                      allowClear
                      placeholder={t('services.form.placeholders.selectCompany')}
                      // value 存 project_id，label 展示公司名
                      options={companies
                        .filter(c => c.project_id !== undefined && c.project_id !== null && String(c.project_id).trim() !== '')
                        .map(c => ({ label: c.company_name, value: Number.isNaN(Number(c.project_id)) ? String(c.project_id) : Number(c.project_id) }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="service_name"
                    label={t('services.form.serviceName')}
                    rules={[{ required: true, message: t('services.form.placeholders.enterServiceName') }]}
                  >
                    <AutoComplete
                      placeholder={t('services.form.placeholders.enterServiceName')}
                      allowClear
                      filterOption={(inputValue, option) =>
                        option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                      options={
                        (function renderOptions() {
                          const [options, setOptions] = React.useState<Array<{ value: string; label: string }>>([]);
                          React.useEffect(() => {
                            serverService.getServiceNameOptions().then((names) => {
                              setOptions(names.map(name => ({ value: name, label: name })));
                            }).catch(() => setOptions([]));
                          }, []);
                          return options;
                        })()
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="service_type"
                    label={t('services.form.serviceType')}
                    rules={[{ required: true, message: t('services.form.placeholders.selectServiceType') }]}
                  >
                    <Select placeholder={t('services.form.placeholders.selectServiceType')}>
                      <Option value="ai_platform">{t('services.form.serviceTypeOptions.ai_platform')}</Option>
                      <Option value="webhook">{t('services.form.serviceTypeOptions.webhook')}</Option>
                      <Option value="api">{t('services.form.serviceTypeOptions.api')}</Option>
                      <Option value="proxied_ai_platform">proxied_ai_platform</Option>
                      <Option value="database">{t('services.form.serviceTypeOptions.database')}</Option>
                      <Option value="custom">{t('services.form.serviceTypeOptions.custom')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="service_url"
                label={t('services.form.serviceUrl')}
                rules={[{ required: true, message: t('services.form.placeholders.enterServiceUrl') }]}
              >
                <Input placeholder={t('services.form.placeholders.enterServiceUrl')} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="home_page_link"
                    label={t('services.form.homePageLink')}
                    tooltip={t('services.form.placeholders.homePageLinkTooltip')}
                  >
                    <Input placeholder={t('services.form.placeholders.enterHomePageLink')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="health_link"
                    label={t('services.form.healthLink')}
                    tooltip={t('services.form.placeholders.healthLinkTooltip')}
                  >
                    <Input placeholder={t('services.form.placeholders.enterHealthLink')} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="description"
                label={t('services.form.description')}
              >
                <TextArea
                  rows={3}
                  placeholder={t('services.form.placeholders.enterDescription')}
                />
              </Form.Item>
            </TabPane>

            <TabPane tab={t('services.form.auth')} key="auth">
              <Form.Item
                name="auth_type"
                label={t('services.form.authType')}
                rules={[{ required: true, message: t('services.form.placeholders.selectServiceType') }]}
              >
                <Select placeholder={t('services.form.authType')}>
                  <Option value="api_key">{t('services.form.authTypeOptions.api_key')}</Option>
                  <Option value="app_key">{t('services.form.authTypeOptions.app_key')}</Option>
                  <Option value="oauth2">{t('services.form.authTypeOptions.oauth2')}</Option>
                  <Option value="bearer">{t('services.form.authTypeOptions.bearer')}</Option>
                  <Option value="basic">{t('services.form.authTypeOptions.basic')}</Option>
                </Select>
              </Form.Item>

              {/* 根据认证类型渲染不同的表单字段 */}
              {authType === 'app_key' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="x_app_id"
                      label={t('services.form.xAppId')}
                      rules={[{ required: true, message: t('services.form.placeholders.enterXAppId') }]}
                    >
                      <Input placeholder={t('services.form.placeholders.enterXAppId')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="x_app_key"
                      label={t('services.form.xAppKey')}
                      rules={[{ required: true, message: t('services.form.placeholders.enterXAppKey') }]}
                    >
                      <Input.Password placeholder={t('services.form.placeholders.enterXAppKey')} />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {authType === 'api_key' && (
                <Form.Item
                  name="api_key"
                  label={t('services.form.apiKey')}
                  rules={[{ required: true, message: t('services.form.placeholders.enterApiKey') }]}
                >
                  <TextArea
                    rows={3}
                    placeholder={t('services.form.placeholders.enterApiKey')}
                    showCount
                    maxLength={1000}
                  />
                </Form.Item>
              )}

              {authType === 'oauth2' && (
                <>
                  <Form.Item
                    name="oauth2_token_url"
                    label={t('services.form.oauth2TokenUrl')}
                    rules={[{ required: true, message: t('services.form.placeholders.enterOAuth2TokenUrl') }]}
                  >
                    <Input placeholder={t('services.form.placeholders.enterOAuth2TokenUrl')} />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="client_id"
                        label={t('services.form.clientId')}
                        rules={[{ required: true, message: t('services.form.placeholders.enterClientId') }]}
                      >
                        <Input placeholder={t('services.form.placeholders.enterClientId')} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="client_secret"
                        label={t('services.form.clientSecret')}
                        rules={[{ required: true, message: t('services.form.placeholders.enterClientSecret') }]}
                      >
                        <Input.Password placeholder={t('services.form.placeholders.enterClientSecret')} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              {authType === 'bearer' && (
                <Form.Item
                  name="bearer_token"
                  label={t('services.form.bearerToken')}
                  rules={[{ required: true, message: t('services.form.placeholders.enterBearerToken') }]}
                >
                  <TextArea
                    rows={3}
                    placeholder={t('services.form.placeholders.enterBearerToken')}
                  />
                </Form.Item>
              )}

              {authType === 'basic' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="username"
                      label={t('services.form.username')}
                      rules={[{ required: true, message: t('services.form.placeholders.enterUsername') }]}
                    >
                      <Input placeholder={t('services.form.placeholders.enterUsername')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="password"
                      label={t('services.form.password')}
                      rules={[{ required: true, message: t('services.form.placeholders.enterPassword') }]}
                    >
                      <Input.Password placeholder={t('services.form.placeholders.enterPassword')} />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Form.Item
                name="additional_headers"
                label={t('services.form.additionalHeaders')}
              >
                <TextArea
                  rows={3}
                  placeholder={t('services.form.placeholders.enterAdditionalHeaders')}
                />
              </Form.Item>
            </TabPane>

            <TabPane tab={t('services.form.ai')} key="ai">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="platform_type"
                    label={t('services.form.platformType')}
                  >
                    <Select placeholder="请选择平台类型">
                      <Option value="openai">OpenAI</Option>
                      <Option value="claude">Claude</Option>
                      <Option value="dify">Dify</Option>
                      <Option value="ibuild">IBuild</Option>
                      <Option value="ibuild_proxy">IBuildProxy</Option>
                      <Option value="custom">Custom</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="app_name"
                    label={t('services.form.appName')}
                  >
                    <Input placeholder={t('services.form.placeholders.enterAppName')} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="app_id"
                label={t('services.form.appId')}
              >
                <Input placeholder={t('services.form.placeholders.enterAppId')} />
              </Form.Item>
            </TabPane>

            <TabPane tab={t('services.form.advanced')} key="advanced">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="timeout_seconds"
                    label={t('services.form.timeoutSeconds')}
                  >
                    <Input placeholder={t('services.form.placeholders.enterTimeout')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="retry_count"
                    label={t('services.form.retryCount')}
                  >
                    <Input placeholder={t('services.form.placeholders.enterRetryCount')} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="is_active"
                    label={t('services.form.isActive')}
                    valuePropName="checked"
                  >
                    <Switch defaultChecked />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="is_file_storage_provider"
                    label="文件存储提供商 (File Storage Provider)"
                    valuePropName="checked"
                    tooltip="标识此服务是否为文件存储提供商服务"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          </Tabs>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0, marginTop: 16 }}>
            <Space>
              <Button onClick={closeModal}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingService ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 参数管理模态框 */}
      {showParamManager && selectedServiceId && (
        <Modal
          title={`服务参数管理 - ${selectedServiceName}`}
          open={showParamManager}
          onCancel={closeParamManager}
          footer={null}
          width="90%"
          style={{ top: 20 }}
        >
          <ServiceParamManager
            serviceId={selectedServiceId}
            serviceName={selectedServiceName}
            onClose={closeParamManager}
          />
        </Modal>
      )}
    </div>
  );
};

export default ServiceManager;
